import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { divisionBracket } from '../kits/stackKit';
import { BASELINE_FROM_TOP, estimateMeasure, TEXT_BOX_HEIGHT } from '../kits/specs';
import type { PrimitiveSpec, TextMeasurer } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ToolRecipe } from '../types';

/**
 * Long division ⟌ — the classic "guzinta" setup (CATALOG M1a): a divisor to
 * the left of an upright, the dividend under a vinculum, the quotient above
 * it, and an optional "R n" remainder to the right. Built on
 * stackKit.divisionBracket (one stroke-only Path: the upright rising into the
 * horizontal vinculum, whose length re-derives to the dividend width).
 *
 * THE LAYOUT.
 *   quotient   above the vinculum, RIGHT-aligned to the dividend (units digit
 *              over units digit — 96 ÷ 4 = 24 sets the 4 over the 6).
 *   ───────    the vinculum (top of the bracket path).
 *   divisor )  dividend         both share the dividend's baseline; the divisor
 *              sits just left of the curve, the dividend at bracket.contentLeft
 *              just right of it.
 *                       R n     the remainder, only when one is present.
 *
 * THE PRINT-KEY LAW (mirrors slotKit.underlineSlot.showAnswer): the quotient
 * text is ALWAYS emitted and always param-tagged; showQuotient=false renders
 * it visible:false, so the answer still round-trips in the design JSON and the
 * teacher's answer-key print is one toggle, not a retype. (A hidden text is
 * not hit-tested, so it can't be double-clicked while blanked — exactly right
 * for a worksheet.)
 *
 * divisor, dividend, quotient and the remainder value are all param-tagged for
 * double-click edit-in-place. Emits Path + FabricText only — plain fabric JSON.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the fraction.ts/handwriting.ts pattern).
// ---------------------------------------------------------------------------

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value (= no remainder / blank). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

// ---------------------------------------------------------------------------
// Text measurement — canvas TextMetrics when available, estimate fallback.
// ---------------------------------------------------------------------------

function makeMeasurer(family: string): TextMeasurer {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return estimateMeasure;
    return (text, fontSize) => {
      ctx.font = `${fontSize}px "${family}", sans-serif`;
      const w = ctx.measureText(text).width;
      return Number.isFinite(w) && w >= 0 ? w : estimateMeasure(text, fontSize);
    };
  } catch {
    return estimateMeasure;
  }
}

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

const INK = '#1e293b';
const DEFAULT_FONT = 'Arial';
const FONT_SIZE = 120;

const longDivision: ToolRecipe = {
  id: 'long-division',
  domain: 'math',
  tags: ['division', 'long-division', 'arithmetic', 'notation', 'worksheet', 'answer-key'],
  name: 'Long division',
  category: 'math',
  version: 1,
  description:
    'Long-division bracket with the divisor, dividend, quotient on top and an optional remainder. Hide the answer for a worksheet, show it for the key. Double-click any number to change it.',
  defaults: {
    divisor: '4',
    dividend: '96',
    quotient: '24',
    remainder: '',
    showQuotient: true,
    color: INK,
    fontFamily: DEFAULT_FONT,
  },
  schema: [
    {
      key: 'dividend',
      label: 'Number being divided',
      type: 'text',
      help: 'Goes under the bar (the dividend).',
    },
    {
      key: 'divisor',
      label: 'Divided by',
      type: 'text',
      help: 'Goes to the left of the bracket (the divisor).',
    },
    {
      key: 'quotient',
      label: 'Answer on top',
      type: 'text',
      help: 'The quotient, written above the bar.',
    },
    {
      key: 'showQuotient',
      label: 'Show the answer',
      type: 'boolean',
      help: 'Turn off for a worksheet — the answer stays saved for the key.',
    },
    {
      key: 'remainder',
      label: 'Remainder',
      type: 'text',
      help: 'Shown as "R n" to the right. Leave empty for none.',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
  ],
  async generate(params) {
    const divisor = rawStr(params.divisor, '4');
    const dividend = rawStr(params.dividend, '96');
    const quotient = rawStr(params.quotient, '24');
    const remainder = rawStr(params.remainder, '');
    const showQuotient = bool(params.showQuotient, true);
    const color = str(params.color, INK);
    const family = str(params.fontFamily, DEFAULT_FONT);
    const measure = makeMeasurer(family);

    // The bracket spans the dividend's text box; a blank dividend still yields
    // a usable bracket one em wide.
    const boxH = FONT_SIZE * TEXT_BOX_HEIGHT;
    const strokeWidth = Math.max(3, FONT_SIZE * 0.05);
    const divW = dividend ? measure(dividend, FONT_SIZE) : FONT_SIZE * 0.6;

    const bracket = divisionBracket({
      dividendWidth: divW,
      height: boxH,
      color,
      strokeWidth,
    });
    const specs: PrimitiveSpec[] = [...bracket.specs];

    // Dividend: under the vinculum (box top on the vinculum at y=0), left edge
    // just right of the curve at bracket.contentLeft.
    specs.push({
      kind: 'text',
      text: dividend,
      x: bracket.contentLeft,
      y: 0,
      fontSize: FONT_SIZE,
      fontFamily: family,
      fill: color,
      originX: 'left',
      originY: 'top',
      param: 'dividend',
    });

    // Divisor: left of the upright, sharing the dividend's baseline.
    specs.push({
      kind: 'text',
      text: divisor,
      x: -FONT_SIZE * 0.12,
      y: 0,
      fontSize: FONT_SIZE,
      fontFamily: family,
      fill: color,
      originX: 'right',
      originY: 'top',
      param: 'divisor',
    });

    // Quotient: above the vinculum, right-aligned to the dividend's right edge.
    // Placed by BASELINE, not box: originY:'bottom' would float the digits a
    // full descender-space (~0.25em) above the bar (digits carry no descender),
    // opening an unsightly gap. Dropping the alphabetic baseline a small
    // clearance above the vinculum sets the digits snug over the bar instead.
    // Always emitted + tagged; hidden (visible:false) is the print-key blank.
    specs.push({
      kind: 'text',
      text: quotient,
      x: bracket.contentLeft + divW,
      y: -FONT_SIZE * (BASELINE_FROM_TOP + 0.1),
      fontSize: FONT_SIZE,
      fontFamily: family,
      fill: color,
      originX: 'right',
      originY: 'top',
      visible: showQuotient,
      param: 'quotient',
    });

    // Remainder "R n" to the right, only when present. The "R" is a static
    // label; the number carries the param tag so it edits to the value alone.
    if (remainder) {
      const rLabelX = bracket.width + FONT_SIZE * 0.35;
      specs.push({
        kind: 'text',
        text: 'R',
        x: rLabelX,
        y: 0,
        fontSize: FONT_SIZE,
        fontFamily: family,
        fill: color,
        originX: 'left',
        originY: 'top',
      });
      specs.push({
        kind: 'text',
        text: remainder,
        x: rLabelX + measure('R', FONT_SIZE) + FONT_SIZE * 0.16,
        y: 0,
        fontSize: FONT_SIZE,
        fontFamily: family,
        fill: color,
        originX: 'left',
        originY: 'top',
        param: 'remainder',
      });
    }

    // Children carry absolute coords; Group derives the bbox and the caller
    // places it (mirrors every other recipe).
    const group = new Group(assembleSpecs(specs));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, longDivision, params);
  },
};

registerRecipe(longDivision);
